import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ThemedTypography from "./ThemedTypography";
import ThemedIconButton from "./ThemedIconButton";
import MobileViewFileDialog from "./dialogs/MobileViewFileDialog";
import * as mime from "react-native-mime-types";
import heic2any from "heic2any";
import axios from "axios";
import { decoratedFetch, decoratedAxiosPost } from "../req_utils";
import { createAutogeneratedLog } from "../log_utils";
import {
  DARK_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  GENESIS_LOGO_COLOR,
  SUPPORTED_PREVIEW_FILE_TYPES,
  SUPPORTED_UPLOAD_FILE_TYPES,
  MAX_UPLOAD_SIZE_MB,
  MAX_UPLOAD_SIZE,
  MAX_NUM_FILES,
} from "../constants";

const PROGRESS_INCREMENT = 2;
const PROGRESS_TICK = 30;

const MobileFileList = ({ open, projectId }) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const employee = useSelector((state) => state.employee);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileLink, setPreviewFileLink] = useState("");
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [selectedListItemIdx, setSelectedListItemIdx] = useState(-1);
  const timerRef = useRef(null);
  const fileAlreadyDeleted = useRef(false);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const handleDeleteFile = (listItemIdx) => {
    decoratedFetch(`/delete_project_files/${projectId}`, {
      method: "POST",
      body: JSON.stringify({
        files: [
          {
            id: files[listItemIdx].id,
            s3_key: files[listItemIdx].s3_key,
          },
        ],
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setFiles(files.filter((file, i) => i !== listItemIdx));
        setSnackbarMessage("Deleted file");
      })
      .catch((error) => {
        console.error(`Error deleting file: ${error}`);
        setSnackbarMessage("An error occurred when deleting file");
      });
  };

  const reset = useCallback(() => {
    setSelectedListItemIdx(-1);
    clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(0);
    setTimeout(() => (fileAlreadyDeleted.current = false), 100);
  }, []);

  const start = useCallback(
    (listItemIdx) => {
      timerRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            if (!fileAlreadyDeleted.current) {
              fileAlreadyDeleted.current = true;
              handleDeleteFile(listItemIdx);
              reset();
            }
            return 0;
          }
          return prevProgress + PROGRESS_INCREMENT;
        });
      }, PROGRESS_TICK);
    },
    [reset, handleDeleteFile],
  );

  const handlePressStart = (listItemIdx) => {
    setSelectedListItemIdx(listItemIdx);
    if (!timerRef.current) start(listItemIdx);
  };

  const handlePressEnd = () => {
    if (!fileAlreadyDeleted.current) reset();
  };

  const listProjectFiles = () => {
    decoratedFetch(`/list_project_files/${projectId}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setFiles(Array.isArray(data) ? data : data.files || []);
        setLoading(false);
        setBackdropOpen(false);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load files");
        setLoading(false);
        setBackdropOpen(false);
        console.error(`Error listing files: ${error}`);
      });
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listProjectFiles();
  }, [open]);

  const handleFilePreview = async (file, index = 0) => {
    if (!SUPPORTED_PREVIEW_FILE_TYPES.includes(file.type)) {
      setSnackbarMessage("File type not supported for previews");
      return;
    }
    setPreviewFile(file);
    setCurrentFileIndex(index);
    try {
      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_url_for_download",
        {
          s3_key: file.s3_key,
        },
      );
      setPreviewFileLink(data.presigned_url);
    } catch (error) {
      setPreviewFileLink("");
      setPreviewFile(null);
      setSnackbarMessage("An error occurred when previewing file");
      console.error("Error previewing file:", error);
    }
  };

  const handleNavigateToFile = async (newIndex) => {
    if (newIndex < 0 || newIndex >= files.length) return;

    const targetFile = files[newIndex];
    if (!SUPPORTED_PREVIEW_FILE_TYPES.includes(targetFile.type)) {
      setSnackbarMessage("File type not supported for previews");
      return;
    }

    setCurrentFileIndex(newIndex);
    setPreviewFile(targetFile);
    try {
      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_url_for_download",
        {
          s3_key: targetFile.s3_key,
        },
      );
      setPreviewFileLink(data.presigned_url);
    } catch (error) {
      setPreviewFileLink("");
      setPreviewFile(null);
      setSnackbarMessage("An error occurred when previewing file");
      console.error("Error previewing file:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const clearPreviewState = () => {
    setPreviewFile(null);
    setPreviewFileLink("");
  };

  const handleAddFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const convertHeicToJpeg = async (heicFile) => {
    try {
      const blob = await heic2any({
        blob: heicFile,
        toType: "image/jpeg",
        quality: 0.8,
      });
      return new File([blob], heicFile.name.replace(/\.heic$/, ".jpeg"), {
        type: "image/jpeg",
      });
    } catch (error) {
      console.error("Error converting HEIC to JPEG:", error);
      return heicFile; // Return the original file if conversion fails
    }
  };

  const handleFileUpload = async (event) => {
    setBackdropOpen(true);
    let files = Array.from(event.target.files);

    if (files.length === 0) {
      return;
    }

    if (files.length > MAX_NUM_FILES) {
      setSnackbarMessage(`Max number of files (${MAX_NUM_FILES}) exceeded`);
      return;
    }

    const uploadSize = files.reduce(
      (totalSize, file) => totalSize + file.size,
      0,
    );
    if (uploadSize > MAX_UPLOAD_SIZE) {
      setSnackbarMessage(`Max upload size (${MAX_UPLOAD_SIZE_MB}MB) exceeded`);
      return;
    }

    // Check if there are any HEIC files
    let containsHeic = files.some((file) => file.type === "image/heic");

    if (containsHeic) {
      setSnackbarMessage("Converting HEIC to JPEG...");

      // Convert HEIC files to JPEG
      files = await Promise.all(
        files.map((file) =>
          file.type === "image/heic" ? convertHeicToJpeg(file) : file,
        ),
      );

      setSnackbarMessage("");
    }

    try {
      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_urls_for_upload",
        {
          project_id: projectId,
          files: files.map((file) => ({
            name: file.name,
            type: file.type,
          })),
        },
      );

      await Promise.all(
        files.map((file, i) =>
          axios.put(data[i].presigned_url, file, {
            headers: {
              "Content-Type": file.type,
            },
          }),
        ),
      );

      await decoratedAxiosPost("/create_project_files_metadata", {
        project_id: projectId,
        files: files.map((file, i) => ({
          name: file.name,
          type: mime.extension(file.type),
          s3_key: data[i].s3_key,
        })),
      });

      setSnackbarMessage(`File${files.length > 1 ? "s" : ""} uploaded`);

      createAutogeneratedLog(
        projectId,
        employee?.id,
        files.length === 1
          ? `File \"${files[0].name}\" uploaded by ${employee.first_name} ${employee.last_name}`
          : `${files.length} files uploaded by ${employee.first_name} ${employee.last_name}`,
      )
        .then((response) => {
          if (response.status !== 201) {
            throw new Error("Error occurred");
          }
        })
        .catch((error) => {
          console.error(`Error creating log: ${error}`);
        });

      listProjectFiles();
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.msg ||
          `An error occurred when uploading file${files.length > 1 ? "s" : ""}`,
      );
      console.error(
        `Error uploading file${files.length > 1 ? "s" : ""}:`,
        error,
      );
    }
  };

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backdropOpen}
        onClick={() => setBackdropOpen(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MobileViewFileDialog
        file={previewFile}
        url={previewFileLink}
        handleClose={() => clearPreviewState()}
        files={files}
        currentFileIndex={currentFileIndex}
        onNavigateToFile={handleNavigateToFile}
      />
      <Snackbar
        sx={{
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        open={snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
      {files?.length ? (
        <List sx={{ py: 0 }}>
          {files.map((file, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ p: 0 }}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <ListItemButton
                      key={index}
                      onClick={() => handleFilePreview(files[index], index)}
                    >
                      <ListItemText
                        primary={file.name}
                        secondary={
                          <>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Type: "}</strong>
                              {file?.type}
                            </p>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Created Date: "}</strong>
                              {formatDate(file.created_at)}
                            </p>
                          </>
                        }
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: "bold",
                            color: darkThemeEnabled
                              ? DARK_THEME_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            color: darkThemeEnabled
                              ? DARK_THEME_DISABLED_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                      />
                    </ListItemButton>
                    <ThemedIconButton
                      onMouseDown={() => handlePressStart(index)}
                      onTouchStart={() => handlePressStart(index)}
                      onMouseUp={() => handlePressEnd()}
                      onTouchEnd={() => handlePressEnd()}
                      sx={{
                        width: "54px",
                        height: "54px",
                        "& svg": {
                          fontSize: "24px",
                        },
                        ml: "5px",
                        mr: "10px",
                      }}
                    >
                      <DeleteIcon />
                    </ThemedIconButton>
                  </Grid>
                  {progress > 0 && selectedListItemIdx === index && (
                    <Grid item xs={12} sx={{ mt: "-4px" }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Grid>
                  )}
                </Grid>
              </ListItem>
              {index < files.length - 1 && (
                <Divider
                  component="li"
                  sx={{
                    backgroundColor: darkThemeEnabled
                      ? DARK_THEME_INPUT_OUTLINE_COLOR
                      : "inherited",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </List>
      ) : loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress size="50px" />
        </Box>
      ) : backdropOpen ? null : (
        <ThemedTypography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          No files
        </ThemedTypography>
      )}
      <Fab
        color="primary"
        aria-label="add"
        size="medium"
        sx={{ position: "fixed", bottom: 20, right: 20 }}
        onClick={handleAddFile}
      >
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept={SUPPORTED_UPLOAD_FILE_TYPES}
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default MobileFileList;
